import React, { useEffect, useRef, useState } from "react";
import { NavLink, useHistory, useLocation } from "react-router-dom";
import { useUser } from "./Context/UserContext";
import { useUI } from "./Context/UIContext";
import Icon from "./ui/Icon";
import Logo from "./ui/Logo";
import { getInitials } from "./Profile";
import { gsap, prefersReducedMotion } from "../lib/motion";
import "../styles/sidebar.css";

/* One icon per menu, matched on the server-provided menu name. */
function iconForMenu(menuName = "") {
    const n = menuName.toLowerCase();
    if (n === "home") return "home";
    if (n.includes("performance")) return "gauge";
    if (n.includes("tracker")) return "list";
    if (n.includes("team")) return "users";
    if (n.includes("bench")) return "briefcase";
    if (n.includes("unfreeze")) return "snowflake";
    if (n.includes("extra")) return "settings";
    if (n.includes("report")) return "report";
    if (n.includes("skill")) return "layers";
    return "layers";
}

export default function Sidebar() {
    const { menus, userInfo, logoutUser } = useUser();
    const {
        sidebarOpen,
        closeSidebar,
        sidebarCollapsed,
        expandSidebar,
        toggleSidebarCollapsed,
    } = useUI();
    const history = useHistory();
    const location = useLocation();
    const [openMenu, setOpenMenu] = useState(null);
    const sidebarRef = useRef(null);

    // Stagger the menu items each time the mobile drawer slides open.
    useEffect(() => {
        if (!sidebarOpen || prefersReducedMotion() || !sidebarRef.current) return undefined;
        const items = sidebarRef.current.querySelectorAll(".sidebar-menu > li");
        const tween = gsap.from(items, {
            x: -14,
            opacity: 0,
            duration: 0.32,
            stagger: 0.045,
            delay: 0.08,
            clearProps: "transform,opacity",
        });
        return () => tween.kill();
    }, [sidebarOpen]);

    const handleLogout = () => {
        logoutUser();
        // replace, not push: the page we are leaving must not stay in the back stack
        history.replace("/login");
    };

    const safeMenus = menus || [];

    const parentMenus = safeMenus.filter(m => !m.parentMenuId);
    const childMenus = safeMenus.filter(m => m.parentMenuId);

    /* A group whose routePath the server left null still needs somewhere to go.
       The SPA registers a landing page for it, so the heading is a real link
       rather than a dead menu item (SM-HR-009, SM-GEN-003). */
    const GROUP_LANDING = {
        "Skill Set Reports": "/skill-set-reports",
    };
    const landingFor = parent => {
        if (parent.routePath) return parent.routePath;
        if (GROUP_LANDING[parent.menuName]) return GROUP_LANDING[parent.menuName];

        // Unknown group: fall back to its first child so the heading still
        // goes somewhere real rather than rendering a link with no target.
        const firstChild = childMenus.find(
            c => Number(c.parentMenuId) === Number(parent.menuId) && c.routePath
        );
        return firstChild ? firstChild.routePath : null;
    };

    /* Keep the group containing the current page open, so the child reports are
       visible in the rail instead of hidden behind a collapsed heading. */
    const currentPath = (location.pathname || "").toLowerCase();

    useEffect(() => {
        const activeChild = childMenus.find(
            c => c.routePath && c.routePath.toLowerCase() === currentPath
        );

        const activeParent =
            activeChild ||
            parentMenus.find(p => {
                const landing = landingFor(p);
                return landing && landing.toLowerCase() === currentPath && !p.routePath;
            });

        if (!activeParent) return;

        const parentId = activeChild
            ? Number(activeChild.parentMenuId)
            : Number(activeParent.menuId);

        setOpenMenu(prev => (prev === parentId ? prev : parentId));
    }, [currentPath, safeMenus.length]);

    const toggleMenu = (menuId) => {
        setOpenMenu(openMenu === menuId ? null : menuId);
    };

    // Collapsed rail: clicking a group first expands the rail, then opens it.
    const handleParentClick = (menuId) => {
        if (sidebarCollapsed) {
            expandSidebar();
            setOpenMenu(menuId);
            return;
        }
        toggleMenu(menuId);
    };

    return (
        <>
            {/* Mobile drawer scrim */}
            <div
                className={`sidebar-scrim ${sidebarOpen ? "visible" : ""}`}
                onClick={closeSidebar}
                aria-hidden="true"
            />

            <aside
                id="app-sidebar"
                ref={sidebarRef}
                className={`sidebar ${sidebarOpen ? "open" : ""}`}
            >
                {/* Brand header — official Mindteck logo with the product name below */}
                <div className="sidebar-brand">
                    <span className="sidebar-brand-stack">
                        <Logo size={27} showWordmark={false} className="sidebar-brand-logo" />
                        <span className="sidebar-brand-title">Skill Management</span>
                    </span>

                    {/* Compact monogram — shown only when collapsed to the icon rail */}
                    <Logo variant="mark" size={30} className="sidebar-brand-mark" />

                    {/* Desktop: collapse toggle */}
                    <button
                        type="button"
                        className="sidebar-collapse-btn"
                        onClick={toggleSidebarCollapsed}
                        aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                        title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                    >
                        <Icon name={sidebarCollapsed ? "chevronRight" : "chevronLeft"} size={16} />
                    </button>

                    {/* Mobile: close drawer */}
                    <button
                        type="button"
                        className="sidebar-close-btn"
                        onClick={closeSidebar}
                        aria-label="Close navigation menu"
                    >
                        <Icon name="x" size={18} />
                    </button>
                </div>

                {/* Long labels ellipsis inside the rail ("Skill Performance
                    Dashboard (Self)"), so every row carries its full name as a
                    tooltip — not only on the collapsed icon rail. */}
                <ul className="sidebar-menu">
                    <li className="sidebar-section-label">Workspace</li>

                    {parentMenus.map(parent => {
                        const children = childMenus.filter(
                            c => Number(c.parentMenuId) === Number(parent.menuId)
                        );
                        const landing = landingFor(parent);
                        const expanded = openMenu === parent.menuId;
                        const submenuId = `sidebar-submenu-${parent.menuId}`;

                        return (
                            <li key={parent.menuId}>
                                {children.length > 0 ? (
                                    <>
                                        {/* The heading navigates to the group's own page and
                                            the chevron expands the children, so neither the
                                            group nor its reports is a dead end. */}
                                        <div
                                            className={`sidebar-group-row ${expanded ? "expanded" : ""}`}
                                        >
                                            <NavLink
                                                to={landing}
                                                activeClassName="active"
                                                className="sidebar-link sidebar-group-link"
                                                onClick={closeSidebar}
                                                title={parent.menuName}
                                            >
                                                <span className="sidebar-link-icon">
                                                    <Icon name={iconForMenu(parent.menuName)} size={18} />
                                                </span>
                                                <span className="sidebar-link-label">{parent.menuName}</span>
                                            </NavLink>

                                            <button
                                                type="button"
                                                className={`sidebar-parent sidebar-group-toggle ${expanded ? "expanded" : ""}`}
                                                onClick={() => handleParentClick(parent.menuId)}
                                                aria-expanded={expanded && !sidebarCollapsed}
                                                aria-controls={submenuId}
                                                aria-label={
                                                    expanded
                                                        ? `Collapse ${parent.menuName}`
                                                        : `Expand ${parent.menuName}`
                                                }
                                                title={
                                                    expanded
                                                        ? `Collapse ${parent.menuName}`
                                                        : `Expand ${parent.menuName}`
                                                }
                                            >
                                                <span className="sidebar-chevron">
                                                    <Icon name="chevronDown" size={15} />
                                                </span>
                                            </button>
                                        </div>

                                        {expanded && !sidebarCollapsed && (
                                            <ul className="sidebar-submenu" id={submenuId}>
                                                {children.map(child => (
                                                    <li key={child.menuId}>
                                                        <NavLink
                                                            to={child.routePath}
                                                            activeClassName="active"
                                                            className="sidebar-link sidebar-sublink"
                                                            onClick={closeSidebar}
                                                            title={child.menuName}
                                                        >
                                                            <span className="sidebar-link-icon">
                                                                <Icon name={iconForMenu(child.menuName)} size={16} />
                                                            </span>
                                                            <span className="sidebar-link-label">{child.menuName}</span>
                                                        </NavLink>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </>
                                ) : (
                                    parent.routePath ? (
                                        <NavLink
                                            to={parent.routePath}
                                            activeClassName="active"
                                            className="sidebar-link"
                                            onClick={closeSidebar}
                                            title={parent.menuName}
                                        >
                                            <span className="sidebar-link-icon">
                                                <Icon name={iconForMenu(parent.menuName)} size={18} />
                                            </span>
                                            <span className="sidebar-link-label">{parent.menuName}</span>
                                        </NavLink>
                                    ) : (
                                        <span className="sidebar-link" title={parent.menuName}>
                                            <span className="sidebar-link-icon">
                                                <Icon name={iconForMenu(parent.menuName)} size={18} />
                                            </span>
                                            <span className="sidebar-link-label">{parent.menuName}</span>
                                        </span>
                                    )
                                )}
                            </li>
                        );
                    })}
                </ul>

                <div className="sidebar-footer">
                    {userInfo ? (
                        <div className="sidebar-user">
                            <span className="sidebar-user-avatar" aria-hidden="true">
                                {getInitials(userInfo.name)}
                            </span>
                            <span className="sidebar-user-meta">
                                <span className="sidebar-user-name" title={userInfo.name}>
                                    {userInfo.name}
                                </span>
                                <span className="sidebar-user-role">
                                    {(userInfo.roles && userInfo.roles[0]) || "Employee"}
                                </span>
                            </span>
                            <button
                                onClick={handleLogout}
                                className="sidebar-signout"
                                aria-label="Sign out"
                                title="Sign out"
                            >
                                <Icon name="logout" size={17} />
                            </button>
                        </div>
                    ) : (
                        <button onClick={handleLogout} className="logout-btn">
                            <Icon name="logout" size={17} />
                            <span>Sign out</span>
                        </button>
                    )}
                </div>
            </aside>
        </>
    );
}
